import { useMutation } from '@tanstack/react-query';
import type { CountryCode } from 'libphonenumber-js';
import { lazy, Suspense, useCallback, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lazily } from 'react-lazily';
import { isPossiblePhoneNumber, type Value } from 'react-phone-number-input';

import {
	type GetRequiredInfoResponse,
	type PostErrorFullRegisterResponse,
	postRequiredInfo,
	type PostRequiredInfoRequest,
} from 'services/idm';

import { useCaseInsensitiveParam } from 'hooks/use-case-insensitive-params';
import { useAppTranslation } from 'hooks/use-translates';

import { ApiErrorBlock } from '@login/components/api-error-block';
import { BirthPlaceBlock } from '@login/components/birth-place-block';
import { AddressFields } from '@login/components/fields/address-fields';
import { DateOfBirthFields } from '@login/components/fields/date-of-birth-fields';
import { FormBox } from '@login/components/form-box';
import { FormHeader } from '@login/components/form-header';
import { apiErrorHandler } from '@login/helpers';
import type { Country } from '@login/helpers/arrays';
import { useApiRedirect } from '@login/hooks/use-api-redirect';
import { useIdmFormErrors } from '@login/hooks/use-idm-form-errors';
import { useScrollToFirstError } from '@login/hooks/use-scroll-to-first-error';
import { EMAIL_PATTERN } from '@login/lib/constants';
import {
	REQUIRED_INFO_ERROR_KEYS,
	REQUIRED_INFO_FIELD_ORDER,
} from '@login/patterns/form-messages';

import styles from './required-info-form.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const Divider = lazy(() => import('core/generic/elements/divider'));
const InfoRequiredFields = lazy(
	() => import('@login/components/info-required-fields/info-required-fields')
);
const { Insert, Phone } = lazily(() => import('core/generic/elements/form'));

type RequiredInfoFormUrls = {
	registerUrl?: string;
	signInUrl?: string;
	forgottenPasswordUrl?: string;
	privacyPolicyUrl?: string;
	supportName?: string;
	siteName?: string;
	minimumAge?: string;
};

type ChannelSpecificFields = {
	showCountyOfBirthForSpecifiedCountries: string[];
	showAndRequireCityOfBirth?: boolean;
	showAndRequireCountryOfBirth?: boolean;
	showAndRequireCountyOfBirth?: boolean;
};

type RequiredInfoFormProps = {
	urls: RequiredInfoFormUrls | null;
	prefilledData: GetRequiredInfoResponse['data'];
	token?: string | null;
	countries: Country[] | null;
	channelSpecificFields: ChannelSpecificFields | undefined;
};

const DEFAULT_COUNTRY: CountryCode = 'IT';
const EMPTY_COUNTRIES: Country[] = [];

// The Phone element requires an onChange prop, but the value is driven by
// react-hook-form's `control`. Declared once so the prop stays stable.
const NOOP_PHONE_CHANGE = () => undefined;

export const RequiredInfoForm = ({
	urls,
	token,
	countries,
	prefilledData,
	channelSpecificFields,
}: RequiredInfoFormProps) => {
	const { i18n } = useTranslation();
	const { t } = useAppTranslation(urls);
	const { goToRedirect } = useApiRedirect();
	const returnUrl = useCaseInsensitiveParam('returnUrl');

	const apiErrors = useIdmFormErrors({
		t,
		messageKeys: REQUIRED_INFO_ERROR_KEYS,
	});

	const methods = useForm<PostRequiredInfoRequest>({
		defaultValues: prefilledData,
		mode: 'onBlur',
	});
	const {
		control,
		handleSubmit,
		register,
		formState: { errors },
	} = methods;

	const countryList = countries ?? EMPTY_COUNTRIES;
	const selectedCountry = useWatch({ control, name: 'address.countryCode' });

	const defaultCountryCode = useMemo<CountryCode>(() => {
		const match = countryList.find(
			(country) => country.shortName === selectedCountry
		);
		return (match?.shortName as CountryCode) ?? DEFAULT_COUNTRY;
	}, [countryList, selectedCountry]);

	const controls = prefilledData?.controls;

	const dobEnabled = useMemo(
		() => ({
			day: !!controls?.dayOfBirthEnabled,
			month: !!controls?.monthOfBirthEnabled,
			year: !!controls?.yearOfBirthEnabled,
		}),
		[controls]
	);

	const isDOBDisabled =
		!dobEnabled.day && !dobEnabled.month && !dobEnabled.year;

	const birthPlaceDefaults = useMemo(
		() => ({
			birthCountry: prefilledData?.address?.birthCountry || '',
			birthCounty: prefilledData?.address?.birthCounty || '',
			birthCity: prefilledData?.address?.birthCity || '',
		}),
		[prefilledData]
	);

	const birthPlaceControls = useMemo(
		() => ({
			countryOfBirthEnabled: controls?.countryOfBirthEnabled,
			countyOfBirthEnabled: controls?.countyOfBirthEnabled,
			cityOfBirthEnabled: controls?.cityOfBirthEnabled,
		}),
		[controls]
	);

	const minimumAge = urls?.minimumAge ? Number(urls.minimumAge) : undefined;

	useScrollToFirstError({
		errors,
		apiFieldErrors: apiErrors.fields,
		hasBanner: !!apiErrors.banner,
		fieldOrder: REQUIRED_INFO_FIELD_ORDER,
	});

	// `mutate`, not `mutateAsync`: failures are handled in onError, and an
	// awaited rejection here would surface as an unhandled promise rejection.
	const { mutate: submitRequiredInfo, isPending: isSubmitting } = useMutation(
		{
			mutationKey: ['required-info', i18n.language, token, returnUrl],
			mutationFn: (formData: PostRequiredInfoRequest) =>
				postRequiredInfo({
					data: { ...formData, returnUrl },
					lang: i18n.language,
					token,
				}),
			onSuccess: (response) => {
				if (response.action === 'Redirect' && response.redirectUrl) {
					goToRedirect({
						action: response.action,
						url: response.redirectUrl,
					});
				}
			},
			onError: (err) => {
				const error = err as unknown as PostErrorFullRegisterResponse;
				apiErrorHandler(error, i18n.language);
				apiErrors.setFromApiError(error);
			},
		}
	);

	const handleOnSubmit = useCallback(
		(formData: PostRequiredInfoRequest) => {
			apiErrors.clear();
			submitRequiredInfo(formData);
		},
		[apiErrors, submitRequiredInfo]
	);

	const dobLabels = useMemo(
		() => ({
			legend: t('requiredinfo.dob_label_info'),
			hint: isDOBDisabled
				? t('requiredinfo.dob_help_disabled_info')
				: t('requiredinfo.dob_help_info'),
			dayPlaceholder: t('requiredinfo.day_placeholder_info'),
			monthPlaceholder: t('requiredinfo.month_placeholder_info'),
			yearPlaceholder: t('requiredinfo.year_placeholder_info'),
			dayRequired: t('requiredinfo.day_required_error_info'),
			monthRequired: t('requiredinfo.month_required_error_info'),
			yearRequired: t('requiredinfo.year_required_error_info'),
			tooYoung: t('requiredinfo.too_young_error_full'),
		}),
		[t, isDOBDisabled]
	);

	const addressLabels = useMemo(
		() => ({
			country: t('requiredinfo.country_label_info'),
			countryPlaceholder: t('requiredinfo.country_placeholder_info'),
			countryRequired: t('requiredinfo.country_required_error_info'),
			addressLine1: t('requiredinfo.address_line1_label_info'),
			addressLine1Required: t(
				'requiredinfo.address_line1_required_error_info'
			),
			addressLine2: t('requiredinfo.address_line2_label_info'),
			addressLine3: t('requiredinfo.address_line3_label_info'),
			city: t('requiredinfo.city_label_info'),
			cityRequired: t('requiredinfo.city_required_error_info'),
			postcode: t('requiredinfo.postcode_label_info'),
			postcodeRequired: t('requiredinfo.postcode_required_error_info'),
			postcodeInvalid: (postcodeSample: string) =>
				t('fullregister.postcode_invalid_error_full', {
					postcodeSample,
				}),
		}),
		[t]
	);

	const addressDefaults = useMemo(
		() => ({
			addressLine1: prefilledData?.address?.addressLine1 || '',
			addressLine2: prefilledData?.address?.addressLine2 || '',
			addressLine3: prefilledData?.address?.addressLine3 || '',
			city: prefilledData?.address?.city || '',
			postcode: prefilledData?.address?.postcode || '',
		}),
		[prefilledData]
	);

	const showBirthPlace =
		!!channelSpecificFields?.showAndRequireCityOfBirth ||
		!!channelSpecificFields?.showAndRequireCountryOfBirth;

	const bannerMessage = apiErrors.banner || errors.birthDate?.message || '';

	return (
		<Suspense fallback={null}>
			<FormProvider {...methods}>
				<form
					onSubmit={handleSubmit(handleOnSubmit)}
					id="form-profile"
					noValidate
				>
					<FormBox>
						<FormHeader headerTitle={t('common.register_tab')} />
						<div className={styles['form-box-content']}>
							<ApiErrorBlock apiErrorMessage={bannerMessage} />

							<div className={styles.registerDataSection}>
								<div className="body-large bold">
									{t('requiredinfo.required_info_title_info')}
								</div>
								<div className="body-medium">
									{t(
										'requiredinfo.required_info_description_info'
									)}
								</div>
							</div>

							<InfoRequiredFields />

							<DateOfBirthFields
								labels={dobLabels}
								minimumAge={minimumAge}
								enabled={dobEnabled}
								resolveError={apiErrors.getFieldError}
							/>

							{!errors.birthDate?.message && (
								<>
									<div className={styles.registerDataSection}>
										<Insert
											{...register('firstName', {
												required: t(
													'requiredinfo.first_name_required_error_info'
												),
											})}
											defaultValue={
												prefilledData?.firstName || ''
											}
											id="firstName"
											label={t(
												'requiredinfo.first_name_label_info'
											)}
											required={
												!!controls?.firstNameEnabled
											}
											disabled={
												!controls?.firstNameEnabled
											}
											error={apiErrors.getFieldError(
												errors.firstName?.message,
												'FirstName'
											)}
											errorIcon="AlertCircleFilled"
										/>
										<Insert
											{...register('lastName', {
												required: t(
													'requiredinfo.last_name_required_error_info'
												),
											})}
											defaultValue={
												prefilledData?.lastName || ''
											}
											id="lastName"
											label={t(
												'requiredinfo.last_name_label_info'
											)}
											required={
												!!controls?.lastNameEnabled
											}
											disabled={
												!controls?.lastNameEnabled
											}
											error={apiErrors.getFieldError(
												errors.lastName?.message,
												'LastName'
											)}
											errorIcon="AlertCircleFilled"
										/>
									</div>

									<div className={styles.registerDataSection}>
										<Insert
											{...register('email', {
												required: t(
													'requiredinfo.email_required_error_info'
												),
												pattern: {
													value: EMAIL_PATTERN,
													message: t(
														'requiredinfo.email_invalid_error_info'
													),
												},
											})}
											type="email"
											defaultValue={
												prefilledData?.email || ''
											}
											id="email"
											label={t(
												'requiredinfo.email_label_info'
											)}
											required={!!controls?.emailEnabled}
											disabled={!controls?.emailEnabled}
											hint={t(
												'requiredinfo.email_hint_message'
											)}
											error={apiErrors.getFieldError(
												errors.email?.message,
												'Email'
											)}
											errorIcon="AlertCircleFilled"
										/>
									</div>

									<AddressFields
										labels={addressLabels}
										countries={countryList}
										defaults={addressDefaults}
										resolveError={apiErrors.getFieldError}
									/>

									<div className={styles.registerDataSection}>
										<Phone
											rules={{
												required: t(
													'requiredinfo.mobile_phone_required_error_info'
												),
												validate: (value: string) =>
													isPossiblePhoneNumber(
														value
													) ||
													t(
														'fullregister.mobile_phone_invalid_error_full'
													),
											}}
											label={t(
												'requiredinfo.mobile_phone_label_info'
											)}
											id="mobilePhone"
											name="mobilePhone"
											error={apiErrors.getFieldError(
												errors.mobilePhone?.message,
												'MobilePhone'
											)}
											errorIcon="AlertCircleFilled"
											defaultValue={
												prefilledData?.mobilePhone as Value
											}
											defaultCountry={defaultCountryCode}
											required={true}
											control={control}
											onChange={NOOP_PHONE_CHANGE}
										/>
									</div>

									<Divider />

									{showBirthPlace &&
										channelSpecificFields && (
											<div
												className={
													styles.registerDataSection
												}
											>
												<BirthPlaceBlock
													countriesWithCounty={
														channelSpecificFields.showCountyOfBirthForSpecifiedCountries
													}
													countries={countryList}
													errors={errors}
													channelSpecificFields={
														channelSpecificFields
													}
													defaultValues={
														birthPlaceDefaults
													}
													controls={
														birthPlaceControls
													}
												/>
											</div>
										)}

									<Button
										type="submit"
										label={t(
											'requiredinfo.register_button_info'
										)}
										isLoading={isSubmitting}
										disabled={isSubmitting}
										variant="primary"
										fullWidth={true}
										className={styles.submitButton}
										dimension="large"
									/>
								</>
							)}
						</div>
					</FormBox>
				</form>
			</FormProvider>
		</Suspense>
	);
};

export default RequiredInfoForm;
